import { Autocomplete, Button, ButtonGroup, Checkbox, Container, Dialog, DialogTitle, FormControl, InputAdornment, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Paper, Popover, Select, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TablePaginationActions, TableRow, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalArrayOfObj, equalObj, formatPercentage, isNumber, LoadingPage, parseNumber, selectAllOnFocus, SetState } from "./Common";
import { CompaniesSelect, Company } from "./Companies";
import { Client, ClientsSelect } from "./Clients";
import { ClientDetails, ClientDetailsSelect } from "./ClientDetails";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { ArrowDropDownIcon, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { calculateFinancialSummary, DefaultFinancialSummary, FinancialSummary, FinancialSummaryTable, Item, ItemsTable, mapDbItemsToLocal, mapDbRequestItemsToQuoteItems } from "./Items";
import { Supplier } from "./Suppliers";
import { CreditHTML, DeliveryHTML, DownloadPDFButton, InvoiceHTML, pdfFileName, QuoteHTML } from "./PDFs/TemplatesGen";
import { Request } from "./Requests";
import { Add, Edit, Pageview } from "@mui/icons-material";
import { UserContext, userIsEmployeeOrAdmin } from "./Users";
import { ItemDelivery, ItemDeliveryDate } from "./QuotesDelivery";

export interface Quote {
    quote_no: string;
	quote_date: string; // timestamp
	po_no: string;
	po_date: string; // timestamp
	comments: string;
	delivered: number;
    cancelled: number;
    confirmed_for_delivery: number;
	payment_term: number;
    valid_until: string; // timestamp
    quote_title: string;
    business_unit: string;
	client_detail_id: number;
    vat_rate: number;
    vat_rate_text: string;
    service_fee: number;
    service_fee_text: string;
	service_fee_on_vat_total: number;
	service_fee_includes_vat: number;
	round_values: number;
	show_vat_breakdown: number;
}

export const CREATE_CLIENT_DETAIL_ID = -1;

export interface QuoteFormSettings {
    default_supplier: string;
    show_supplier_column: boolean;
}

export function mergeQuotes(old: Array<Quote>, toAdd: Array<Quote>): Array<Quote> {
    var result = [...old];
    for (let q of toAdd) {
        if (result.find((p) => p.quote_no === q.quote_no) === undefined) {
            result.push(q);
        }
    }
    return result;
}

export type SearchState = "editing" | "found" | "empty";

export function QuoteEditPreviewButton({ quote_no, iconOnly, suffix = "", navParams = {} }: { quote_no: string, iconOnly: boolean, suffix?: string, navParams?: any}) {
    const auth = useContext(UserContext);
    return <Button component={Link} to={"/quotes/" + encodeURIComponent(quote_no)} fullWidth={iconOnly} variant={iconOnly ? "contained" : "text"} color="primary">
        {
            !iconOnly ? quote_no + suffix : userIsEmployeeOrAdmin(auth) ? <Edit /> : <Pageview />
        }
    </Button>
}

export function QuotesTable({ quotes, foundQuotes, searchState, navParams, page, setPage } : { quotes: Array<Quote>, foundQuotes: Array<Quote>, searchState: SearchState, navParams: any, page: number, setPage: SetState<number> } ) {
    const navigate = useNavigate();
    const auth = useContext(UserContext);

    const handlePageChange = useCallback((_: any, page: number) => {
        setPage(page);
    }, [setPage]);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Quote No.</TableCell>
                                    <TableCell width="20%">Quote Date</TableCell>
                                    <TableCell width="45%">Title</TableCell>
                                    <TableCell width="5%">
                                        {
                                            userIsEmployeeOrAdmin(auth) && <Button variant="contained" onClick={() => navigate("/quotes/0", { state: navParams })} fullWidth>
                                                <Add />
                                            </Button>
                                        }
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {searchState === "empty"
                                    ? <TableRow>
                                        <TableCell>Nothing Found</TableCell>
                                    </TableRow>
                                    : (foundQuotes.length === 0 ? quotes : foundQuotes).map((row, idx) => (
                                        <TableRow key={row.quote_no + idx}>
                                            <TableCell>{row.quote_no}</TableCell>
                                            <TableCell>{row.quote_date}</TableCell>
                                            <TableCell>{row.quote_title}</TableCell>
                                            <TableCell>
                                                <QuoteEditPreviewButton quote_no={row.quote_no} iconOnly={true} />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TablePagination
                                        rowsPerPageOptions={[]}
                                        colSpan={5}
                                        count={foundQuotes.length === 0 ? -1 : foundQuotes.length}
                                        rowsPerPage={foundQuotes.length === 0 ? 50 : foundQuotes.length}
                                        page={page}
                                        slotProps={{
                                            select: {
                                            inputProps: {
                                                'aria-label': 'rows per page',
                                            },
                                            native: true,
                                            },
                                        }}
                                        onPageChange={handlePageChange}
                                        ActionsComponent={TablePaginationActions}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const QuotesPage = () => {
    const navParams = useParams();
    const { companyIdText, clientIdText, clientDetailIdText, pageIdText } = navParams;

    const [quotes, setQuotes] = useState<Array<Quote>>([]);
    const [foundQuotes, setFoundQuotes] = useState<Array<Quote>>([]);
    const [title, setTitle] = useState<string>("");
    const [filterText, setFilterText] = useState("");
    const [searchState, setSearchState] = useState<SearchState>("editing");
    const [snackbar, setSnackbar] = useState("");
    const limit = 50;
    const [pageId, setPageId] = useState(parseInt(pageIdText || "0"));
    const scrollLimitFound = useRef(false);

    useEffect(() => {
        const fetchClients = async () => {
            var query = `?limit=${limit}&offset=${limit * pageId}`;
            var newTitle = "";
            if (clientDetailIdText !== undefined) {
                query += "&client_detail_id=" + encodeURIComponent(clientDetailIdText);
                const response = await fetch("/api/v1/client_details/" + encodeURIComponent(clientDetailIdText));
                const data = (await response.json())?.result;
                newTitle = ((data?.client_detail?.person || "") + " (" + (data?.client?.name || "") + ")");
            } else if (clientIdText !== undefined) {
                query += "&client_id=" + encodeURIComponent(clientIdText);
                const response = await fetch("/api/v1/clients/" + encodeURIComponent(clientIdText));
                const data = (await response.json())?.result;
                newTitle = (data?.name || "");
            } else if (companyIdText !== undefined) {
                query += "&company_id=" + encodeURIComponent(companyIdText);
                const response = await fetch("/api/v1/companies/" + encodeURIComponent(companyIdText));
                const data = (await response.json())?.result;
                newTitle = (data?.name || "");
            }
            if (newTitle !== "") {
                setTitle(newTitle + " - ")
            }
            const clientResponse = await fetch("/api/v1/quotes" + query);
            const clientData = await clientResponse.json();
            if ((clientData.result || []).length === 0) {
                scrollLimitFound.current = true;
            }
            setQuotes(clientData.result || []);
            if (clientData.error) {
                setSnackbar(clientData.error);
            }
        };
        fetchClients();
    }, [clientDetailIdText, clientIdText, companyIdText, limit, pageId]);

    const findQuotes = async () => {
        var query = "";
        if (clientDetailIdText !== undefined) {
            query = "client_detail_id=" + encodeURIComponent(clientDetailIdText);
        } else if (clientIdText !== undefined) {
            query = "client_id=" + encodeURIComponent(clientIdText);
        } else if (companyIdText !== undefined) {
            query = "company_id=" + encodeURIComponent(companyIdText);
        }
        if (filterText === "") {
            setFoundQuotes([]);
            setSearchState("editing");
        } else {
            const response = await fetch(`/api/v1/quotes-query?${query}&search=` + encodeURIComponent(filterText));
            const data = await response.json();
            if (data.error) {
                setSnackbar(data.error);
            } else if (data.result && data.result.length !== 0) {
                setFoundQuotes(data.result);
                setSearchState("found");
            } else {
                setFoundQuotes([]);
                setSearchState("empty");
            }
        }
    }

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={8}> <h1> {title} Quotes </h1> </Grid>
                <Grid size={4}> 
                    <TextField
                        id="filterText"
                        label="Search"
                        variant="outlined"
                        onChange={(e) => { setFilterText(e.target.value); setFoundQuotes([]); setSearchState("editing") }}
                        onKeyUp={e => e.key === "Enter" ? findQuotes() : 0}
                        fullWidth
                        value={filterText} />
                </Grid>
                <Grid size={12}>
                    <QuotesTable quotes={quotes} foundQuotes={foundQuotes} searchState={searchState} navParams={navParams} page={pageId} setPage={setPageId}/>
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 8 }}> </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}

export function FinancialCalculationSettings({ quote, setQuote }: { quote: Partial<Quote>, setQuote: SetState<Partial<Quote>> }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    
    return (
        <Fragment>
            <Button aria-describedby="FinancialCalculationSettings" variant="contained" onClick={(ev) => setAnchorEl(ev.currentTarget)} fullWidth>
                Finance Settings
            </Button>
            <Popover 
                id="FinancialCalculationSettings"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <List>
                    <ListItem key="service_fee_includes_vat">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, service_fee_includes_vat: quote.service_fee_includes_vat === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Switch edge="start" checked={quote.service_fee_includes_vat === 1}/>
                            </ListItemIcon>
                            <ListItemText> Service Fee Includes VAT </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="service_fee_on_vat_total">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, service_fee_on_vat_total: quote.service_fee_on_vat_total === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Switch edge="start" checked={quote.service_fee_on_vat_total === 1}/>
                            </ListItemIcon>
                            <ListItemText> Service Fee On VAT </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="round_values">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, round_values: quote.round_values === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Switch edge="start" checked={quote.round_values === 1} />
                            </ListItemIcon>
                            <ListItemText> Round Values </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="show_vat_breakdown">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, show_vat_breakdown: quote.show_vat_breakdown === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Switch edge="start" checked={quote.show_vat_breakdown === 1} />
                            </ListItemIcon>
                            <ListItemText> Show VAT Breakdown </ListItemText>
                        </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export function QuoteStatus({ quote, setQuote }: { quote: Partial<Quote>, setQuote: SetState<Partial<Quote>> }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <Button aria-describedby="QuoteStatus" variant="contained" onClick={(ev) => setAnchorEl(ev.currentTarget)} fullWidth>
                Status
            </Button>
            <Popover 
                id="QuoteStatus"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <List>
                    <ListItem key="delivery">
                        <ListItemButton component={Link} to={"/quotes/delivery/" + encodeURIComponent(quote.quote_no || "")}>
                            <ListItemText> Delivered/Paid </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="cancelled">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, cancelled: quote.cancelled === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Checkbox edge="start" checked={quote.cancelled === 1}/>
                            </ListItemIcon>
                            <ListItemText> Cancelled </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="confirmed">
                        <ListItemButton onClick={() => setQuote(quote => ({...quote, confirmed_for_delivery: quote.confirmed_for_delivery === 1 ? 0 : 1}))}>
                            <ListItemIcon>
                                <Checkbox edge="start" checked={quote.confirmed_for_delivery === 1}/>
                            </ListItemIcon>
                            <ListItemText> Delivery Confirmed </ListItemText>
                        </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export function FormSettings({ settings, setSettings }: { settings: QuoteFormSettings, setSettings: SetState<QuoteFormSettings> }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    
    return (
        <Fragment>
            <Button aria-describedby="FormSettings" variant="contained" onClick={(ev) => setAnchorEl(ev.currentTarget)} fullWidth>
                Form Settings
            </Button>
            <Popover 
                id="FormSettings"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <List>
                    <ListItem key="show_supplier_column">
                        <ListItemButton onClick={() => setSettings({...settings, show_supplier_column: !settings.show_supplier_column})}>
                            <ListItemIcon>
                                <Switch edge="start" checked={settings.show_supplier_column}/>
                            </ListItemIcon>
                            <ListItemText> Show Supplier Column </ListItemText>
                        </ListItemButton>
                    </ListItem>
                    <ListItem key="supplier_default">
                        <ListItemButton>
                            <ListItemIcon>
                                <TextField 
                                    value={settings.default_supplier}
                                    onFocus={selectAllOnFocus}
                                    onChange={(ev) => setSettings({...settings, default_supplier: ev.target.value})}
                                    label="Default Supplier"
                                />
                            </ListItemIcon>
                        </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export function QuoteForm({ quote, setQuote, companies, clients, clientDetails, client, setClient, clientDetail, setClientDetail } : { quote: Partial<Quote>, setQuote: SetState<Partial<Quote>>, companies: Array<Company>, clients: Array<Client>, clientDetails: Array<ClientDetails>, company: Partial<Company>, setCompany: SetState<Partial<Company>>, client: Partial<Client>, setClient: SetState<Partial<Client>>, clientDetail: Partial<ClientDetails>, setClientDetail: SetState<Partial<ClientDetails>> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6, lg: 2 }}>
                <CompaniesSelect companies={companies} client={client} setClient={setClient} setClientDetail={setClientDetail} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 2 }}>
                <ClientsSelect clients={clients} clientDetail={clientDetail} setClientDetail={setClientDetail} setQuote={setQuote} />
            </Grid>
            <Grid size={{ xs: 12, md: 12, lg: 6 }}>
                <ClientDetailsSelect clientDetails={clientDetails} quote={quote} setQuote={setQuote} />
            </Grid>
            <Grid size={{ xs: 12, md: 12, lg: 2 }}>
                <FinancialCalculationSettings quote={quote} setQuote={setQuote}/>
            </Grid>
            {
                clientDetail !== undefined 
                    ? <Grid size={12}>
                        <TableContainer component={Paper}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell width={"48"}>Person</TableCell>
                                    <TableCell>
                                        <TextField 
                                            id="detail person"
                                            size="small"
                                            variant="standard"
                                            onFocus={selectAllOnFocus}
                                            onChange={(e) => setClientDetail(detail => ({ ...detail, person: e.target.value }))}
                                            fullWidth
                                            value={clientDetail.person || ""} />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>
                                        <TextField 
                                            id="detail contact"
                                            size="small"
                                            variant="standard"
                                            onFocus={selectAllOnFocus}
                                            onChange={(e) => setClientDetail(detail => ({ ...detail, contact: e.target.value })) }
                                            fullWidth
                                            value={clientDetail.contact || ""} />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Address</TableCell>
                                    <TableCell>
                                        <TextField 
                                            id="detail address"
                                            size="small"
                                            variant="standard"
                                            onFocus={selectAllOnFocus}
                                            onChange={(e) => setClientDetail(detail => ({ ...detail, address: e.target.value })) }
                                            fullWidth
                                            value={clientDetail.address || ""} />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        </TableContainer>
                      </Grid>
                    : <></>
            }
            <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                    id="quote_title"
                    label="Title"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setQuote(quote => ({ ...quote, quote_title: e.target.value }))}
                    fullWidth
                    value={quote.quote_title || ""} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                    id="comments"
                    label="Comments"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setQuote(quote => ({ ...quote, comments: e.target.value }))}
                    fullWidth
                    value={quote.comments || ""} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <Autocomplete
                    id="business_unit"
                    freeSolo
                    options={["Canteen", "Entertainment", "Incentives"]}
                    value={quote.business_unit || ""}
                    onInputChange={(_, value) => setQuote(quote => ({ ...quote, business_unit: value || "" })) }
                    onChange={(_, value) => setQuote(quote => ({ ...quote, business_unit: value || "" })) }
                    renderInput={(params: any) => <TextField {...params} label="Business Unit" />}/>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                    id="vat_rate"
                    label="VAT Rate"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setQuote(quote => ({ ...quote, vat_rate: parseNumber(e.target.value) / 100, vat_rate_text: e.target.value }))}
                    error={!isNumber(quote.vat_rate_text || "")}
                    fullWidth
                    value={quote.vat_rate_text || ""} 
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }
                    }}/>
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <TextField
                    id="service_fee"
                    label="Service Fee"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setQuote(quote => ({ ...quote, service_fee: parseNumber(e.target.value) / 100, service_fee_text: e.target.value }))}
                    error={!isNumber(quote.service_fee_text || "")}
                    fullWidth
                    value={quote.service_fee_text || ""} 
                    slotProps={{
                        input: {
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }
                    }} />
            </Grid>
            <Grid size={{ xs: 6, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Quote Date"
                        slotProps={{ textField: { fullWidth: true } }}
                        onAccept={(value) => setQuote(quote => ({ ...quote, quote_date: value?.format("YYYY-MM-DD"), valid_until: value?.add(7, 'days').format("YYYY-MM-DD") }))}
                        value={quote.quote_date === undefined || quote.quote_date === "" ? dayjs() : dayjs(quote.quote_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 6, lg: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Valid Until"
                        slotProps={{ textField: { fullWidth: true } }}
                        onAccept={(value) => setQuote(quote => ({ ...quote, valid_until: value?.format("YYYY-MM-DD") }))}
                        value={quote.valid_until === undefined || quote.valid_until === "" ? dayjs().add(7, 'day') : dayjs(quote.valid_until, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 6, lg: 3 }}>
                <FormControl fullWidth>
                    <InputLabel>Payment Term</InputLabel>
                    <Select
                        label="Payment Term"
                        value={quote.payment_term || 30}
                        onChange={(ev) => setQuote(quote => ({ ...quote, payment_term: ev.target.value }))}
                    >
                        {[7, 14, 30, 60, 90].map((days) => (
                            <MenuItem value={days}>{days} Days</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ md: 6, lg: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="PO Date"
                        slotProps={{ textField: { fullWidth: true } }}
                        onAccept={(value) => setQuote(quote => ({ ...quote, po_date: value?.format("YYYY-MM-DD") }))}
                        value={quote.po_date === undefined || quote.po_date === "" ? dayjs() : dayjs(quote.po_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 6, lg: 3 }}>
                <TextField
                    id="po_no"
                    label="PO Number"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setQuote(quote => ({ ...quote, po_no: e.target.value }))}
                    fullWidth
                    value={quote.po_no || ""} />
            </Grid>
        </Grid>
    )
}

function QuoteSheet({ quote, clientDetails } : { quote: Partial<Quote>, clientDetails: Array<ClientDetails> }) {
    const detail = clientDetails.find((value) => value.id === quote.client_detail_id);
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            {
                detail !== undefined 
                    ? <Grid size={12}>
                        <TableContainer component={Paper}>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell>Person</TableCell>
                                    <TableCell>{detail.person || ""} </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Contact</TableCell>
                                    <TableCell>{detail.contact || ""} </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>Address</TableCell>
                                    <TableCell>{detail.address || ""} </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                        </TableContainer>
                      </Grid>
                    : <></>
            }
            <Grid size={{ md: 6, lg: 4 }}>
                <TextField
                    id="quote_title"
                    label="Title"
                    variant="outlined"
                    slotProps={{ input:{ readOnly: true } }}
                    fullWidth
                    value={quote.quote_title || ""} />
            </Grid>
            <Grid size={{ md: 6, lg: 4 }}>
                <TextField
                    id="comments"
                    label="Comments"
                    variant="outlined"
                    slotProps={{ input:{ readOnly: true } }}
                    fullWidth
                    value={quote.comments || ""} />
            </Grid>
            <Grid size={{ md: 6, lg: 4 }}>
                <TextField
                    id="business_unit"
                    label="Business Unit"
                    variant="outlined"
                    slotProps={{ input:{ readOnly: true } }}
                    fullWidth
                    value={quote.business_unit || ""} />
            </Grid>
            <Grid size={{ md: 6, lg: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Quote Date"
                        slotProps={{ textField: { fullWidth: true, readOnly: true } }}
                        value={quote.quote_date === undefined || quote.quote_date === "" ? dayjs() : dayjs(quote.quote_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 6, lg: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="Valid Until"
                        slotProps={{ textField: { fullWidth: true, readOnly: true } }}
                        value={quote.valid_until === undefined || quote.valid_until === "" ? dayjs().add(7, 'day') : dayjs(quote.valid_until, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={{ md: 6, lg: 4 }}>
                <TextField
                    id="payment_term"
                    label="Payment Term"
                    variant="outlined"
                    slotProps={{ input:{ readOnly: true } }}
                    fullWidth
                    value={(quote.payment_term || 30) + " Days"} />
            </Grid>
            <Grid size={ 6 }>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label="PO Date"
                        slotProps={{ textField: { fullWidth: true, readOnly: true } }}
                        value={quote.po_date === undefined || quote.po_date === "" ? dayjs() : dayjs(quote.po_date, "YYYY-MM-DD")} />
                </LocalizationProvider>
            </Grid>
            <Grid size={ 6 }>
                <TextField
                    id="po_no"
                    label="PO Number"
                    variant="outlined"
                    slotProps={{ input:{ readOnly: true } }}
                    fullWidth
                    value={quote.po_no || ""} />
            </Grid>
        </Grid>
    )
}

function SaveQuotePopover({ saveQuote, edited, quote, details, client, company, items, itemDeliveries, itemDeliveryDate, summary }: { saveQuote: any, edited: boolean, quote: Partial<Quote>, details: Partial<ClientDetails>, client: Partial<Client>, company: Partial<Company>, items: Array<Item>, itemDeliveries: Array<ItemDelivery>, itemDeliveryDate: ItemDeliveryDate, summary: FinancialSummary }) {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    return (
        <Fragment>
            <ButtonGroup variant="contained" fullWidth>
                <Button disabled={!edited} variant="contained" onClick={saveQuote} fullWidth> Save </Button>
                <Button sx={{ width: 32 }} aria-describedby="Products" onClick={(ev) => setAnchorEl(ev.currentTarget)} variant="contained">
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popover
                id="SaveQuote"
                open={anchorEl !== null}
                onClose={() => setAnchorEl(null)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <List>
                    <ListItem>
                        <DownloadPDFButton content={QuoteHTML(quote, details, client, company, items, summary)} filename={pdfFileName(quote, "Quote")}>
                            <ListItemText>Export Quote</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                    <ListItem>
                        <DownloadPDFButton content={DeliveryHTML(quote, details, client, items, itemDeliveries)} filename={pdfFileName(quote, "Delivery")}>
                            <ListItemText>Export Delivery Note</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                    <ListItem>
                        <DownloadPDFButton content={InvoiceHTML(quote, details, client, company, items, summary, itemDeliveries, itemDeliveryDate)} filename={pdfFileName(quote, "Invoice")}>
                            <ListItemText>Export Tax Invoice</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                    <ListItem>
                        <DownloadPDFButton content={CreditHTML(quote, details, client, company, items, summary, itemDeliveries, itemDeliveryDate)} filename={pdfFileName(quote, "Credit")}>
                            <ListItemText>Export Credit Note</ListItemText>
                        </DownloadPDFButton>
                    </ListItem>
                </List>
            </Popover>
        </Fragment>
    )
}

export const QuotePage = () => {
    const { quoteNoText } = useParams();
    const { state } = useLocation();
    const { companyIdText, clientIdText, clientDetailIdText, requestIdText } = state || {};
    const quoteNo = (quoteNoText === undefined || quoteNoText === "0") ? "" : quoteNoText;

    const [companies, setCompanies] = useState<Array<Company>>([]);
    const [clients, setClients] = useState<Array<Client>>([]);
    const [clientDetails, setClientDetails] = useState<Array<ClientDetails>>([]);

    const [quote, setQuote] = useState<Partial<Quote>>({ quote_date: dayjs().format("YYYY-MM-DD"), valid_until: dayjs().add(7, 'day').format("YYYY-MM-DD")  });
    const [company, setCompany] = useState<Partial<Company>>({});
    const [client, setClient] = useState<Partial<Client>>({});
    const [clientDetail, setClientDetail] = useState<Partial<ClientDetails>>({});
    const [items, setItems] = useState<Array<Item>>([]);
    const [itemDeliveryDates, setItemDeliveryDates] = useState<Array<ItemDeliveryDate>>([]);
    const [itemDeliveries, setItemDeliveries] = useState<Array<Array<ItemDelivery>>>([]);
    const [financialSummary, setFinancialSummary] = useState<FinancialSummary>(DefaultFinancialSummary);
    const [suppliers, setSuppliers] = useState<Array<Supplier>>([]);
    const [request, setRequest] = useState<Partial<Request>>({});
    const [savedQuote, setSavedQuote] = useState<Partial<Quote>>({});
    const [savedItems, setSavedItems] = useState<Array<Item>>([]);
    const [savedClientDetail, setSavedClientDetail] = useState<Partial<ClientDetails>>({});
    const [edited, setEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(0);

    const [settings, setSettings] = useState<QuoteFormSettings>({
        show_supplier_column: false,
        default_supplier: "Makro",
    });

    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const navigate = useNavigate();
    const auth = useContext(UserContext);

    // Fetch Company List
    useEffect(() => {
        const fetchData = async () => {
            const companyResponse = await fetch("/api/v1/companies");
            const companyData = await companyResponse.json();
            setCompanies(companyData.result || []);
            if (companyData.error) {
                setSnackbar(companyData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, []);

    // Fetch Partial Company/Client/Details
    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch("/api/v1/client_details/" + parseInt(clientDetailIdText));
            const data = await response.json();
            setClient(data?.result?.client || {});
            setClientDetail(data?.result?.client_detail || {});
            setQuote(quote => ({ ...quote, client_detail_id: data?.result?.client_detail?.id }));
            if (data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        }
        if (clientDetailIdText === undefined) {
            return
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [clientDetailIdText]);

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch("/api/v1/clients/" + parseInt(clientIdText));
            const data = await response.json();
            setClient(data?.result || {});
            setClientDetail(detail => ({ ...detail, client_id: data?.result?.id }));
            if (data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        }
        if (clientIdText === undefined) {
            return;
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [clientIdText]);

    useEffect(() => {
        if (companyIdText !== undefined) {
            setClient(client => ({...client, company_id: parseInt(companyIdText)}));
        }
    }, [companyIdText]);

    // Fetch Header From Request
    useEffect(() => {
        const fetchData = async () => {
            const requestResponse = await fetch("/api/v1/requests/" + parseInt(requestIdText));
            const requestData = await requestResponse.json();
            if (requestData.result) {
                const request: Request = requestData.result.request;
                setRequest(request);
                const client: Client = requestData.result.client;
                setQuote(quote => ({ ...quote, quote_title: request.title, comments: request.comments }))
                setClient(client);
                setClientDetail(detail => ({ ...detail, client_id: client.id }));
            } else {
                setSnackbar(requestData.error);
            }
            setIsLoading(lock => lock - 1);
        }
        if ((requestIdText || "") === "") {
            return
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [requestIdText]);

    useEffect(() => {
        const fetchData = async () => {
            const itemsResponse = await fetch("/api/v1/request-items/" + parseInt(requestIdText));
            const itemsData = await itemsResponse.json();
            if (itemsData.result) {
                setItems(itemsData.result?.map((it: any, idx: number) => mapDbRequestItemsToQuoteItems(it, idx, quote.quote_no || "")) || []);
            } else {
                setSnackbar(itemsData.error);
            }
            setIsLoading(lock => lock - 1);
        };
        if ((requestIdText || "") === "") {
            return
        }
        if ((quote.quote_no || "") === "") {
            return;
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [requestIdText, quote.quote_no]);

    // Fetch Full Quote
    useEffect(() => {
        const fetchData = async () => {
            const quoteResponse = await fetch("/api/v1/quotes/" + encodeURIComponent(quoteNo));
            const quoteData = await quoteResponse.json();
            if (quoteData.result) {
                quoteData.result.quote.service_fee_text = formatPercentage(quoteData.result.quote.service_fee || 250);
                quoteData.result.quote.vat_rate_text = formatPercentage(quoteData.result.quote.vat_rate || 1500);
            }
            setQuote(quoteData?.result?.quote || {});
            setSavedQuote(quoteData?.result?.quote || {});
            setClient(quoteData?.result?.client || {});
            setClientDetail(quoteData?.result?.client_detail || {});
            setSavedClientDetail(quoteData?.result?.client_detail || {});

            if (quoteData.error) {
                setSnackbar(quoteData.error);
            }

            const itemsResponse = await fetch("/api/v1/items_delivered/" + encodeURIComponent(quoteNo));
            const itemsData = await itemsResponse.json();
            setItemDeliveries(itemsData?.result?.items || []);
            setItemDeliveryDates(itemsData?.result?.dates || []);

            setIsLoading(lock => lock - 1);
        }
        if (quoteNo === undefined || quoteNo === "") {
            return
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [quoteNo]);

    // Update Client/Detail
    useEffect(() => {
        const fetchData = async () => {
            if (clientDetail.client_id !== undefined) {
                setClient(clients.find((value) => value.id === clientDetail.client_id) || {});
            }
            if (quote.client_detail_id !== undefined) {
                if (quote.client_detail_id !== CREATE_CLIENT_DETAIL_ID) {
                    const detail = clientDetails.find((value) => value.id === quote.client_detail_id);
                    setClientDetail(detail || {});
                } else {
                    setClientDetail({ client_id: clientDetail.client_id });
                }
            }
        }
        fetchData();
    }, [clientDetail.client_id, clients, clientDetails, quote.client_detail_id]);

    // Update Clients List
    useEffect(() => {
        const fetchData = async () => {
            const clientsResponse = await fetch("/api/v1/clients?company_id=" + client.company_id);
            const clientsData = await clientsResponse.json();
            setClients(clientsData.result || []);
            if (clientsData.error) {
                setSnackbar(clientsData.error);
            }

            const companyResponse = await fetch("/api/v1/companies/" + encodeURIComponent(client.company_id || "0"));
            const companyData = await companyResponse.json();
            setCompany(companyData.result || {});
            if (companyData.error) {
                setSnackbar(companyData.error);
            }

            setQuote(quote => (
                {
                    service_fee: companyData?.result?.service_fee,
                    service_fee_includes_vat: companyData?.result?.service_fee_includes_vat,
                    service_fee_on_vat_total: companyData?.result?.service_fee_on_vat_total,
                    round_values: companyData?.result?.round_values,
                    show_vat_breakdown: companyData?.result?.show_vat_breakdown,
                    vat_rate: companyData?.result?.vat_rate,
                    ...quote,
                    service_fee_text: formatPercentage(quote?.service_fee || companyData?.result?.service_fee),
                    vat_rate_text: formatPercentage(quote?.vat_rate || companyData?.result?.vat_rate),
                }
            ))
        }
        if (client.company_id === undefined) {
            return
        }
        fetchData();
    }, [client.company_id]);

    // Update ClientDetails List
    useEffect(() => {
        const fetchData = async () => {
            const clientDetailsResponse = await fetch("/api/v1/client_details?client_id=" + clientDetail.client_id);
            const clientDetailsData = await clientDetailsResponse.json();
            setClientDetails(clientDetailsData.result || []);
            if (clientDetailsData.error) {
                setSnackbar(clientDetailsData.error);
            }
        }
        if (clientDetail.client_id === undefined) {
            return
        }
        fetchData();
    }, [clientDetail.client_id]);

    // Get New Quote Number
    useEffect(() => {
        const fetchData = async () => {
            if (clientDetail.client_id !== undefined && client.quote_id !== undefined) {
                const quoteCountResponse = await fetch("/api/v1/quotes-count?client_id=" + clientDetail.client_id);
                const quoteCountData = await quoteCountResponse.json();
                setQuote(quote => ({...quote, quote_no: client.quote_id + " " + String((quoteCountData.result || 0) + 1).padStart(3, '0')}))
                if (quoteCountData.error) {
                    setSnackbar(quoteCountData.error);
                }
            }
        }
        if (quoteNo !== "") {
            return
        }
        fetchData();
    }, [clientDetail.client_id, quoteNo, client.quote_id]);

    // Update Quote Items
    useEffect(() => {
        const fetchData = async (unlock: boolean) => {
            const param = quote.quote_no !== undefined ? quote.quote_no : (quoteNo !== undefined && quoteNo !== "" ? quoteNo : "");
            const itemsResponse = await fetch("/api/v1/items/" + encodeURIComponent(param));
            const itemsData = await itemsResponse.json();
            setItems(itemsData?.result?.map(mapDbItemsToLocal) || []);
            setSavedItems(itemsData?.result?.map(mapDbItemsToLocal) || [])
            if (itemsData.error) {
                setSnackbar(itemsData.error);
            }
            if (unlock) {
                setIsLoading(lock => lock - 1);
            }
        }
        const param = quote.quote_no !== undefined ? quote.quote_no : (quoteNo !== undefined && quoteNo !== "" ? quoteNo : "");
        const isRaw = quoteNo !== undefined && quoteNo !== "";
        if (param === undefined || param === "") {
            return;
        }
        if ((requestIdText || "") !== "") {
            return
        }
        if (isRaw) {
            setIsLoading(lock => lock + 1);
        }
        fetchData(isRaw);
    }, [quoteNo, quote.quote_no, requestIdText]);

    // Update Products List
    useEffect(() => {
        const fetchProducts = async () => {
            const suppliersResponse = await fetch("/api/v1/suppliers");
            const suppliersData = await suppliersResponse.json();
            setSuppliers(suppliersData.result || []);
            if (suppliersData.error) {
                setSnackbar(suppliersData.error);
            }
            setIsLoading(lock => lock - 1);
        };
        setIsLoading(lock => lock + 1);
        fetchProducts();
    }, []);

    // Update Financial Summary
    useEffect(() => {
        calculateFinancialSummary(quote, items, setFinancialSummary);
    }, [quote, items]);

    // Update edited State
    useEffect(() => {
        var isEdited = !(equalObj(quote, savedQuote) && equalArrayOfObj(items, savedItems) && equalObj(clientDetail, savedClientDetail));
        setEdited(isEdited);
    }, [savedQuote, quote, savedItems, items, clientDetail, savedClientDetail]);

    const saveQuote = useCallback(async () => {
        if (!quote.client_detail_id) {
            setSnackbar("Missing Client Detail");
            return;
        }

        const clientDetailData = clientDetail.id === CREATE_CLIENT_DETAIL_ID ? { ...clientDetail, id: 0 } : clientDetail;
        const quoteResponse = await fetch("/api/v1/quotes", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({quote: quote, detail: clientDetailData})
        });
        const quoteData = await quoteResponse.json();
        if (quoteData.result) {
            quoteData.result.quote.service_fee_text = formatPercentage(quoteData.result.service_fee || 250);
            quoteData.result.quote.vat_rate_text = formatPercentage(quoteData.result.vat_rate || 1500);
        }
        setQuote(quoteData.result.quote || {});
        setSavedQuote(quoteData.result.quote || {});
        if (quoteData.result.detail.id !== undefined) {
            setClientDetails(details => [ ...details, quoteData.result.detail ]);
        }
        setClientDetail(clientDetail);
        setSavedClientDetail(clientDetail);
        var noErrors = true;
        var quoteNo = "";
        if (quoteData.error) {
            noErrors = false;
            setSnackbar(quoteData.error);
            return;
        } else {
            quoteNo = quoteData.result.quote.quote_no;
        }

        const requestResponse = await fetch("/api/v1/requests", {
            method: (request.id || 0) === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"request": { ...request, quote_no: quoteNo, sent: 0 }})
        });
        const requestData = await requestResponse.json();
        if (requestData.error) {
            noErrors = false;
            setSnackbar(requestData.error);
            return;
        }

        if ((quoteNo || "") !== "") {
            const itemsResponse = await fetch("/api/v1/items/" + encodeURIComponent(quoteNo), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(items)
            });
            const itemsData = await itemsResponse.json();
            setItems(itemsData?.result?.map(mapDbItemsToLocal) || []);
            setSavedItems(itemsData?.result?.map(mapDbItemsToLocal) || []);
            if (itemsData.error) {
                noErrors = false;
                setSnackbar(itemsData.error);
            }
        }

        if (noErrors) {
            if (quoteNoText === "0") {
                navigate("/quotes/" + quoteData.result.quote.quote_no, { replace: true });
            }
            setSnackbar("Information Saved");
        }
    }, [items, quote, quoteNoText, request, navigate, clientDetail]);

    const deleteQuote = useCallback(async () => {
        const quoteResponse = await fetch("/api/v1/quotes/" + encodeURIComponent(quote.quote_no || ""), { method: "DELETE" });
        const quoteData = await quoteResponse.json();
        if (quoteData.result) {
            quoteData.result.service_fee_text = formatPercentage(quoteData.result.service_fee || 250);
            quoteData.result.vat_rate_text = formatPercentage(quoteData.result.vat_rate || 1500);
        }
        var noError = true;
        
        if (quoteData.error) {
            setSnackbar(quoteData.error);
            noError = false;
        }

        const itemsResponse = await fetch("/api/v1/items/" + encodeURIComponent(quote.quote_no || ""), { method: "DELETE" });
        const itemsData = await itemsResponse.json();
        if (itemsData.error) {
            setSnackbar(itemsData.error);
            noError = false;
        }

        if (noError) {
            setQuote(quoteData.result || {});
            setItems(itemsData?.result?.map(mapDbItemsToLocal) || []);
            navigate(-1);
        }
    }, [navigate, quote.quote_no]);

    useEffect(() => {
        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            return event.returnValue = "Unsaved Changes! Leave Without Saving?";
        };

        if (edited) {
            window.addEventListener("beforeunload", handler);
            return () => window.removeEventListener("beforeunload", handler);
        }

        return () => {};
    }, [edited]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                {userIsEmployeeOrAdmin(auth)
                    ? <Fragment>
                        <Grid size={{ xs: 12, md: 12, lg: 4 }}> <h1> {quote.quote_no || "New Quote"} </h1> </Grid>
                        <Grid size={{ xs: 6, md: 3, lg: 2}}>
                            <SaveQuotePopover saveQuote={saveQuote} edited={edited} quote={quote} details={clientDetail} client={client} company={company} items={items} itemDeliveries={itemDeliveries[itemDeliveries.length - 1]} itemDeliveryDate={itemDeliveryDates[itemDeliveryDates.length - 1]} summary={financialSummary} />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3, lg: 2}}>
                            <QuoteStatus quote={quote} setQuote={setQuote}/>
                        </Grid>
                        <Grid size={{ xs: 6, md: 3, lg: 2}}>
                            <FormSettings settings={settings} setSettings={setSettings}/>
                        </Grid>
                        {
                        quoteNo === "" 
                            ? <Grid size={{ xs: 6, md: 3, lg: 2}}>
                                <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                            </Grid>
                            : <Grid size={{ xs: 6, md: 3, lg: 2}}>
                                <Button variant="contained" onClick={() => setRequestDelete(true)} color="error" fullWidth> Delete </Button>
                            </Grid>
                        }
                    </Fragment>
                    : <Grid size={12}> <h1> {quote.quote_no} </h1> </Grid>
                }
                
                <Grid size={12}>
                    {userIsEmployeeOrAdmin(auth)
                        ? <QuoteForm 
                            quote={quote} 
                            setQuote={setQuote} 

                            companies={companies}
                            clients={clients}
                            clientDetails={clientDetails}

                            company={company}
                            setCompany={setCompany}

                            client={client} 
                            setClient={setClient} 
                            
                            clientDetail={clientDetail}
                            setClientDetail={setClientDetail}/>
                        : <QuoteSheet quote={quote} clientDetails={clientDetails} />
                    }
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 2 }}> </Grid>
            <Grid component={Container} container spacing={2}>
                <Grid size={12}>
                    <ItemsTable quote={quote} items={items} setItems={setItems} suppliers={suppliers} settings={settings}/>
                </Grid>
                <Grid size={12}>
                    <FinancialSummaryTable financialSummary={financialSummary} showVatBreakdown={quote.show_vat_breakdown === 1}/>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this quote? </DialogTitle>
                <Button onClick={deleteQuote}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>
    )
}